//LecturePageScroll.js

import { useState, useEffect, useCallback, useRef } from 'react';

function useVirtualizedScrollManager(translations, listRef, rowHeights) {
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [showNewMessageButton, setShowNewMessageButton] = useState(false);

    const listOuterRef = useRef(null);

    // 내부 상태 refs
    const isInitialLoadRef = useRef(true);
    const manualScrollActionRef = useRef(false);
    const autoScrollEnabledRef = useRef(true);
    const scrollLockRef = useRef(false);
    const lastTranslationCountRef = useRef(0);
    const debounceTimerRef = useRef(null);

    // 코얼레서 및 버스트·높이 안정화 디바운서
    const scrollScheduleRef = useRef(null);
    const burstTimerRef = useRef(null);
    const BURST_END_MS = 180;

    const heightSettleTimerRef = useRef(null);
    const HEIGHT_SETTLE_MS = 200;
    const heightSettledRef = useRef(true);

    // 초기 스크롤 완료 플래그
    const initialScrollCompletedRef = useRef(false);

    // 스크롤 위치 저장용
    const currentScrollPositionRef = useRef({ scrollTop: 0, scrollRatio: 0 });

    // 현재 스크롤 위치 계산
    const checkScrollPosition = useCallback(() => {
        const el = listOuterRef.current;
        if (!el) return { isBottom: true, scrollTop: 0, scrollHeight: 0, clientHeight: 0 };

        const { scrollTop, scrollHeight, clientHeight } = el;
        const bottomThreshold = 30;
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
        const isBottom = distanceFromBottom <= bottomThreshold;

        return { isBottom, scrollTop, scrollHeight, clientHeight };
    }, []);

    // 높이 변동 발생 시 호출
    const notifyRowHeightChange = useCallback(() => {
        heightSettledRef.current = false;
        if (heightSettleTimerRef.current) clearTimeout(heightSettleTimerRef.current);
        heightSettleTimerRef.current = setTimeout(() => {
            heightSettledRef.current = true;

            if (isInitialLoadRef.current && !initialScrollCompletedRef.current) {
                performInitialScrollToBottom();
            }
        }, HEIGHT_SETTLE_MS);
    // performInitialScrollToBottom을 deps에 추가하면 translations.length 변경마다
    // notifyRowHeightChange가 재생성되어 연쇄 re-render 발생 가능 → 의도적으로 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 실제 하단 스크롤 수행
    const performScrollToBottom = useCallback(
        (force = false) => {
            if (manualScrollActionRef.current && !force) return;
            if (!autoScrollEnabledRef.current && !force) return;

            const el = listOuterRef.current;
            const lastIndex = translations.length - 1;
            if (!listRef?.current || lastIndex < 0 || !el) return;

            // react-window 최하단 아이템으로 이동
            listRef.current.scrollToItem(lastIndex, 'end');

            // 다음 프레임에서 실제 DOM 스크롤 조정
            requestAnimationFrame(() => {
                if (!el) return;

                requestAnimationFrame(() => {
                    if (!el) return;

                    const bottom = el.scrollHeight - el.clientHeight;
                    el.scrollTop = bottom;

                    setTimeout(() => {
                        setIsAtBottom(true);
                        setShowNewMessageButton(false);

                        if (isInitialLoadRef.current) {
                            initialScrollCompletedRef.current = true;
                            isInitialLoadRef.current = false;
                        }
                    }, 50);
                });
            });
        },
        [translations.length, listRef]
    );

    // 초기 스크롤 전용 함수
    const performInitialScrollToBottom = useCallback(() => {
        if (initialScrollCompletedRef.current) return;
        if (translations.length === 0) return;
        if (!listRef.current || !listOuterRef.current) return;

        let retryCount = 0;
        const maxRetries = 5;

        const tryScroll = () => {
            if (initialScrollCompletedRef.current) return;
            if (!listRef.current || !listOuterRef.current) return;

            const el = listOuterRef.current;
            const lastIndex = translations.length - 1;

            listRef.current.scrollToItem(lastIndex, 'end');

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (!el) return;

                        const bottom = el.scrollHeight - el.clientHeight;
                        el.scrollTop = bottom;

                        setTimeout(() => {
                            const { isBottom } = checkScrollPosition();

                            if (!isBottom && retryCount < maxRetries) {
                                retryCount++;
                                setTimeout(tryScroll, 100);
                            } else {
                                initialScrollCompletedRef.current = true;
                                isInitialLoadRef.current = false;
                                setIsAtBottom(true);
                                setShowNewMessageButton(false);
                            }
                        }, 100);
                    });
                });
            });
        };

        tryScroll();
    }, [translations.length, checkScrollPosition, listRef]);

    // 스크롤 코얼레서
    const scheduleScrollToBottom = useCallback(
        (delay = 0, force = false) => {
            if (manualScrollActionRef.current && !force) return;
            if (!autoScrollEnabledRef.current && !force) return;

            if (scrollScheduleRef.current) clearTimeout(scrollScheduleRef.current);
            scrollScheduleRef.current = setTimeout(() => {
                scrollScheduleRef.current = null;
                performScrollToBottom(force);
            }, delay);
        },
        [performScrollToBottom]
    );

    // 외부 노출용
    const scrollToBottom = useCallback(
        (force = false) => {
            scheduleScrollToBottom(0, force);
        },
        [scheduleScrollToBottom]
    );

    // 리스트 스크롤 이벤트 핸들러
    const handleListScroll = useCallback(
        ({ scrollOffset, scrollUpdateWasRequested }) => {
            if (scrollUpdateWasRequested || scrollLockRef.current) return;

            if (isInitialLoadRef.current && !initialScrollCompletedRef.current) return;

            const { isBottom } = checkScrollPosition();

            setIsAtBottom(isBottom);
            setShowNewMessageButton(!isBottom);

            if (isBottom) {
                autoScrollEnabledRef.current = true;
                manualScrollActionRef.current = false;

                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                    debounceTimerRef.current = null;
                }
            } else {
                autoScrollEnabledRef.current = false;
                manualScrollActionRef.current = true;

                if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = setTimeout(() => {
                    manualScrollActionRef.current = false;
                }, 5000);
            }
        },
        [checkScrollPosition]
    );

    // 높이 안정화 대기 함수
    const waitHeightsThen = useCallback((fn, timeoutMs = 600) => {
        const start = performance.now();
        const poll = () => {
            if (heightSettledRef.current) {
                fn();
                return;
            }
            if (performance.now() - start >= timeoutMs) {
                fn();
                return;
            }
            setTimeout(poll, 50);
        };
        poll();
    }, []);

    // 재연결 시작 시 스크롤 위치 저장
    const handleReconnectionStart = useCallback(() => {
        const { scrollTop, scrollHeight, clientHeight } = checkScrollPosition();
        const ratio = scrollTop / Math.max(1, scrollHeight - clientHeight);
        currentScrollPositionRef.current = { scrollTop, scrollRatio: ratio };
    }, [checkScrollPosition]);

    // 메시지 기준으로 스크롤 위치 저장
    const saveScrollPosition = useCallback(() => {
        const el = listOuterRef.current;
        if (!el || !translations.length) return;

        const centerY = el.scrollTop + el.clientHeight / 2;
        let total = 0;

        for (let i = 0; i < translations.length; i++) {
            const height = rowHeights.current[i] || 100;
            if (total + height > centerY) {
                const groupId = translations[i].groupId;
                currentScrollPositionRef.current = { groupId };
                break;
            }
            total += height;
        }
    }, [translations, rowHeights]);

    // 저장된 메시지 위치로 복원
    const restoreScrollPosition = useCallback(() => {
        scrollLockRef.current = true;
        const { groupId } = currentScrollPositionRef.current;
        const index = translations.findIndex(g => g.groupId === groupId);

        if (index >= 0 && listRef.current) {
            listRef.current.scrollToItem(index, 'center');
        }

        setTimeout(() => {
            scrollLockRef.current = false;
        }, 300);
    }, [translations, listRef]);

    // 초기화 effect
    useEffect(() => {
        isInitialLoadRef.current = true;
        initialScrollCompletedRef.current = false;
        lastTranslationCountRef.current = 0;

        const saved = sessionStorage.getItem('auto_scroll_disabled');
        autoScrollEnabledRef.current = saved !== 'true';

        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (scrollScheduleRef.current) clearTimeout(scrollScheduleRef.current);
            if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
            if (heightSettleTimerRef.current) clearTimeout(heightSettleTimerRef.current);
        };
    }, []);

    // 초기 스크롤 처리
    useEffect(() => {
        if (translations.length === 0) return;
        if (initialScrollCompletedRef.current) return;

        let initTimer = null;
        let attempts = 0;
        const maxAttempts = 20;

        const tryInitScroll = () => {
            attempts++;

            if (!autoScrollEnabledRef.current) return;
            if (initialScrollCompletedRef.current) return;

            const hasListRef = listRef.current !== null;
            const hasOuterRef = listOuterRef.current !== null;

            if (!hasListRef || !hasOuterRef) {
                if (attempts < maxAttempts) {
                    initTimer = setTimeout(tryInitScroll, 150);
                }
                return;
            }

            waitHeightsThen(() => {
                performInitialScrollToBottom();
            });
        };

        initTimer = setTimeout(tryInitScroll, 100);

        return () => {
            if (initTimer) clearTimeout(initTimer);
        };
    }, [translations.length, waitHeightsThen, performInitialScrollToBottom, listRef]);

    // 새 메시지 감지 및 처리
    useEffect(() => {
        const currentCount = translations.length;
        const prevCount = lastTranslationCountRef.current;

        if (currentCount > prevCount && !isInitialLoadRef.current) {
            const wasAtBottomBeforeNewMessage = isAtBottom;

            setTimeout(() => {
                if (isInitialLoadRef.current && !initialScrollCompletedRef.current) {
                    waitHeightsThen(() => {
                        performInitialScrollToBottom();
                    });
                    return;
                }

                if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
                burstTimerRef.current = setTimeout(() => {
                    burstTimerRef.current = null;

                    const shouldAutoScroll = wasAtBottomBeforeNewMessage && autoScrollEnabledRef.current && !manualScrollActionRef.current;

                    if (shouldAutoScroll) {
                        waitHeightsThen(() => scheduleScrollToBottom(0));
                    } else {
                        setShowNewMessageButton(true);
                    }
                }, BURST_END_MS);
            }, 100);
        }

        lastTranslationCountRef.current = currentCount;
    // waitHeightsThen/performInitialScrollToBottom/scheduleScrollToBottom은 translations.length에
    // 이미 의존하는 함수들이라 추가 시 불필요한 재실행 없음 → 명시적 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translations.length, isAtBottom]);

    return {
        isAtBottom,
        showNewMessageButton,
        scrollToBottom,
        handleListScroll,
        handleReconnectionStart,
        restoreScrollPosition,
        saveScrollPosition,
        notifyRowHeightChange,
        setListOuterElement: (el) => { listOuterRef.current = el; },
        listOuterRef,
    };
}

export default useVirtualizedScrollManager;